import type { StreamRole } from '@/types';

// One charted point: per-second bucket with summed tx/rx throughput and the
// receiver's latency/jitter/loss. Shared by the live WS hook and the
// historical metrics view so both render identical series.
export interface LiveSample {
  t: number; // epoch ms, bucketed to the second
  tx: number;
  rx: number;
  download: number | null; // down-direction throughput (receiver's rx side)
  upload: number | null; // up-direction throughput (receiver's tx side)
  latency: number | null; // downlink latency (receiver-measured)
  latencyUp: number | null; // uplink latency (transmitter-measured; only up/both)
  jitter: number | null;
  loss: number | null;
  retransmits: number | null; // TCP retransmitted segments this second (sender-side)
  dropCount: number | null; // packets dropped this second (sum of loss_events gaps)
}

export interface LossEvent {
  t_ms: number; // epoch ms the gap was detected (server-corrected)
  n: number; // packets in this gap
}

export interface MetricLike {
  role: StreamRole;
  ts: string;
  throughput_mbps: number | null;
  latency_ms: number | null;
  jitter_ms: number | null;
  loss_pct: number | null;
  retransmits?: number | null;
  loss_events?: LossEvent[] | null;
  rx_throughput_mbps?: number | null;
  tx_throughput_mbps?: number | null;
  // Needed to attribute a tick to its traffic class (bucketByTrafficType).
  // Optional because the live frames the merged charts consume don't need it.
  stream_id?: string;
}

// How many recent buckets to scan for a matching second. TX and RX come from
// two different machines over two WS connections, so their frames for the same
// second arrive interleaved — the matching bucket is usually, but not always,
// the last one.
const RECENT_BUCKETS = 6;

// A new bucket inherits the previous bucket's values so a bucket that has only
// received one role's frame so far renders the last-known value for the other
// role instead of a drop to zero (which produced the sawtooth). A real frame
// overwrites it the moment it arrives; a genuine 0-throughput frame still shows
// 0 because the agent sends it explicitly.
function emptyAt(t: number, prev?: LiveSample): LiveSample {
  return {
    t,
    tx: prev?.tx ?? 0,
    rx: prev?.rx ?? 0,
    download: prev?.download ?? null,
    upload: prev?.upload ?? null,
    latency: prev?.latency ?? null,
    latencyUp: prev?.latencyUp ?? null,
    jitter: prev?.jitter ?? null,
    loss: prev?.loss ?? null,
    retransmits: prev?.retransmits ?? null,
    dropCount: null,
  };
}

/** Fold one frame into the running sample list, merging TX and RX for the same
 *  second into a single bucket regardless of arrival order. */
export function foldFrame(samples: LiveSample[], f: MetricLike): LiveSample {
  const tSec = Math.floor(new Date(f.ts).getTime() / 1000) * 1000;

  // Find an existing bucket for this second within the recent window.
  let cur: LiveSample | undefined;
  for (let i = samples.length - 1; i >= 0 && i >= samples.length - RECENT_BUCKETS; i--) {
    if (samples[i].t === tSec) {
      cur = samples[i];
      break;
    }
  }
  if (!cur) {
    cur = emptyAt(tSec, samples[samples.length - 1]);
    samples.push(cur);
    // Keep the series time-ordered if a straggler arrived out of order (rare).
    if (samples.length >= 2 && samples[samples.length - 2].t > tSec) {
      samples.sort((a, b) => a.t - b.t);
    }
  }

  const thr = f.throughput_mbps ?? 0;
  // Assign (last value wins), never accumulate — two frames of the same role
  // landing in one second-bucket must not sum into a doubled spike.
  if (f.role === 'transmitter') {
    cur.tx = thr;
    if (f.retransmits != null) cur.retransmits = f.retransmits;
    // The transmitter only measures latency when IT receives — i.e. the
    // uplink leg of an up/both stream. Ignore its 0s on a plain down test.
    if (f.latency_ms != null && f.latency_ms > 0) cur.latencyUp = f.latency_ms;
  } else {
    cur.rx = thr;
    if (f.latency_ms != null) cur.latency = f.latency_ms;
    if (f.jitter_ms != null) cur.jitter = f.jitter_ms;
    if (f.loss_pct != null) cur.loss = f.loss_pct;
    // Clean per-direction split, measured at the receiver: download = what it
    // received, upload = what it sent. Only new agents emit these; legacy
    // frames leave them null and the chart falls back to TX/RX.
    if (f.rx_throughput_mbps != null) cur.download = f.rx_throughput_mbps;
    if (f.tx_throughput_mbps != null) cur.upload = f.tx_throughput_mbps;
  }
  // Drop timeline: sum the gap sizes reported this frame into the bucket, so a
  // marker's size reflects how many packets were lost that second (either role
  // can carry loss_events — the receiving side of the stream measures it).
  if (f.loss_events && f.loss_events.length > 0) {
    const dropped = f.loss_events.reduce((s, e) => s + (e.n || 0), 0);
    if (dropped > 0) cur.dropCount = (cur.dropCount ?? 0) + dropped;
  }
  return cur;
}

/** Batch-bucket an ordered list of frames into chart samples. */
export function bucketPoints(points: MetricLike[]): LiveSample[] {
  const out: LiveSample[] = [];
  for (const p of points) foldFrame(out, p);
  return out;
}

/**
 * Bucket throughput per traffic type, one series per type.
 *
 * The merged Download/Upload lines are the right default for a single-purpose
 * test, but they actively hide the thing a QoS mix is run to show: "total
 * throughput was fine" while the voice stream was starved. Splitting by traffic
 * type is what makes a starved class visible on the chart rather than only in
 * the per-class table.
 *
 * Returns Recharts-shaped rows — {t, "video-stream": 24.8, "bulk-download": 94}
 * — plus the type list, so the caller doesn't have to re-derive it.
 */
export function bucketByTrafficType(
  points: MetricLike[],
  typeByStream: Record<string, string>,
): { rows: Record<string, number>[]; types: string[] } {
  const bySecond = new Map<number, Record<string, number>>();
  const types = new Set<string>();

  for (const p of points) {
    // Receiver rows only: that's the side that measures what actually arrived.
    // Counting both roles would roughly double every figure.
    if (p.role !== 'receiver') continue;
    const type = p.stream_id ? typeByStream[p.stream_id] : undefined;
    if (!type) continue;
    const thr = p.throughput_mbps;
    if (thr == null) continue;

    const t = Math.floor(new Date(p.ts).getTime() / 1000) * 1000;
    if (!Number.isFinite(t)) continue;
    types.add(type);
    const row = bySecond.get(t) ?? { t };
    // Several streams can share a traffic type (e.g. two video clients) — sum
    // them, matching how the class is presented everywhere else.
    row[type] = (row[type] ?? 0) + thr;
    bySecond.set(t, row);
  }

  const rows = [...bySecond.entries()].sort((a, b) => a[0] - b[0]).map(([, r]) => r);
  // Zero-fill: Recharts breaks a line at a missing key, which would read as
  // "this class stopped" when it merely had no sample that second.
  const typeList = [...types];
  for (const r of rows) for (const ty of typeList) if (r[ty] == null) r[ty] = 0;
  return { rows, types: typeList };
}
